import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import ItemDetail from '../ItemDetail';
import OrderCard from '../OrderCard';
import Skeleton from '@mui/material/Skeleton';
import Alert from '../Alert';

// Styles
import styles from './index.module.sass';

const country = process.env.REACT_APP_COUNTRY;

type Props = {
  totals: Object,
  device: Object,
};

export default function OrderTotals({ totals = {}, device, loading = false, state = '' }: Props) {
  const [open, setOpen] = useState(false);

  const {
    modulosDescripcion = '',
    modulosCantidad = '',
    totalAImporte = '',
    totalBImporte = '',
    totalUnidadesAB = '',
    totalAUnidades = '',
    totalBUnidades = '',
    flete = '',
    gastosOperativos = '',
    subtotal = '',
    iva = '',
    saldoActual = '',
    importeAPagar = '',
    coloradoTax = '',
    firma = '',
  } = totals;

  const screenSize = device?.screenSize || 'desktop';

  const { t } = useTranslation('order');

  useEffect(() => {
    if (parseFloat(saldoActual) > 5) setOpen(true);
  }, [saldoActual]);

  return (
    <div className={`${styles.container} ${styles[screenSize]}`}>
      <Alert
        open={open}
        setOpen={setOpen}
        title={t('currentBalanceAlertTitle')}
        text={t('currentBalanceAlertText')}
      />
      <OrderCard title={t('detailTitle')} open={screenSize === 'phone' ? false : true}>
        <div className={styles.orderDet}>
          <div className={styles.detailContainer}>
            <ItemDetail title={t('description')} text={modulosDescripcion} loading={loading} />
            <ItemDetail title={t('modules')} text={modulosCantidad || 0} loading={loading} />
            <ItemDetail title={t('amountA')} text={`$${totalAImporte || 0}`} loading={loading} />
            <ItemDetail title={t('amountB')} text={`$${totalBImporte || 0}`} loading={loading} />
            <ItemDetail title={t('unitsAB')} text={totalUnidadesAB || 0} loading={loading} />
            <ItemDetail title={t('unitsA')} text={totalAUnidades || 0} loading={loading} />
            <ItemDetail title={t('unitsB')} text={totalBUnidades || 0} loading={loading} />
            <ItemDetail title={t('shipment')} text={`$${flete || 0}`} loading={loading} />
            <ItemDetail
              title={t('deliverWithSignature')}
              text={`$${firma || 0}`}
              loading={loading}
            />
          </div>
        </div>
      </OrderCard>
      <OrderCard title={t('summaryTitle')} open={screenSize === 'phone' ? false : true}>
        <div className={styles.orderDet}>
          <div className={styles.detailContainer}>
            <ItemDetail title={t('subtotal')} text={`$${subtotal || 0}`} loading={loading} />
            <ItemDetail title={t('taxes')} text={`$${iva || 0}`} loading={loading} />
            {coloradoTax && (
              <ItemDetail
                title={`${state} ${t('coloradoTax')}`}
                text={`$${coloradoTax || 0}`}
                loading={loading}
              />
            )}
            {saldoActual && (
              <ItemDetail
                title={t('currentBalance')}
                text={`$${saldoActual || 0}`}
                loading={loading}
              />
            )}
          </div>
        </div>

        <div
          className={`${styles.data} ${styles.colorGold} ${country !== 'MX' && styles.finalTotal}`}>
          <div className={`${styles.title}`}>{t('amountToPay')}:</div>
          <div className={`${styles.title}`}>
            {(loading && (
              <Skeleton variant='rectangular' animation='wave' className={styles.loadingText} />
            )) ||
              `$${importeAPagar}` ||
              `$0`}
          </div>
        </div>
      </OrderCard>
    </div>
  );
}
